<template>
  <div>
    <div class="cultura-chart" ref="chartContainer"></div>
    <div
      ref="tooltip"
      style="
        position: fixed;
        pointer-events: none;
        padding: 6px 10px;
        background: white;
        border: 1px solid #ccc;
        border-radius: 6px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.12);
        opacity: 0;
        font-size: 12px;
        transition: opacity 0.15s;
        z-index: 9999;
        left: 0;
        top: 0;
      "
    ></div>
  </div>
</template>

<script>
import * as d3 from "d3";

export default {
  name: "ResultChart",

  props: {
    actual: {
      type: Array,
      required: true,
    },
    deseada: {
      type: Array,
      required: true,
    },
    size: {
      type: String,
      required: false,
    },
    maxValue: {
      type: Number,
      required: true,
    },
    steps: {
      type: Number,
      required: false,
    },
  },

  mounted() {
    window.addEventListener("resize", this.drawChart);
    this.drawChart();
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.drawChart);
  },

  watch: {
    actual() {
      this.drawChart();
    },
    deseada() {
      this.drawChart();
    },
    maxValue() {
      this.drawChart();
    },
  },

  methods: {
    drawChart() {
      if (
        !this.actual ||
        !this.deseada ||
        !this.actual.length ||
        !this.deseada.length
      )
        return;

      const tooltip = d3.select(this.$refs.tooltip);
      d3.select(this.$refs.chartContainer)
        .selectAll("*")
        .remove();

      const container = this.$refs.chartContainer;
      const { width } = container.getBoundingClientRect();
      let height = width * 0.7;
      if (this.size === "sm") height = width * 0.85;
      const size = Math.min(width, height);
      const radius = size * 0.4;

      let fontSize = 14;
      if (this.size === "sm") fontSize = 12;

      const categories = ["Colaboración", "Creación", "Competencia", "Control"];
      const labelColors = ["#6a00ce", "#ff00ff", "#00d8d8", "#FECC0E"];
      const quadFills = ["#f8f8f8", "#ffffff", "#f8f8f8", "#ffffff"];
      const anglesDeg = [225, 315, 45, 135];
      const angles = anglesDeg.map((a) => (a * Math.PI) / 180);

      const svg = d3
        .select(this.$refs.chartContainer)
        .append("svg")
        .attr("viewBox", `0 0 ${width} ${height}`)
        .attr("preserveAspectRatio", "xMidYMid meet")
        .style("width", "100%")
        .style("height", "auto");

      const center = { x: width / 2, y: 15 + height / 2 };

      // ======================
      // ESCALA GLOBAL REDONDEADA
      // ======================

      let STEP_SIZE = this.steps ? this.steps : 20;

      // Redondea al múltiplo de 10 más cercano
      const roundedMax =
        Math.round(this.maxValue / STEP_SIZE) * STEP_SIZE || STEP_SIZE;

      const scale = d3
        .scaleLinear()
        .domain([0, roundedMax])
        .range([0, radius])
        .clamp(true);

      // ======================
      // GRID LEVELS DINÁMICOS (10 en 10)
      // ======================

      const steps = Math.max(1, Math.round(roundedMax / STEP_SIZE));

      const gridLevels = Array.from(
        { length: steps },
        (_, i) => (i + 1) * STEP_SIZE,
      );

      // -------------------------
      // 1. CUADRANTES
      // -------------------------
      [
        { x: -1, y: -1, color: quadFills[0] },
        { x: 0, y: -1, color: quadFills[1] },
        { x: 0, y: 0, color: quadFills[2] },
        { x: -1, y: 0, color: quadFills[3] },
      ].forEach((q) => {
        svg
          .append("rect")
          .attr("x", center.x + q.x * radius)
          .attr("y", center.y + q.y * radius)
          .attr("width", radius)
          .attr("height", radius)
          .attr("fill", q.color);
      });

      // -------------------------
      // 2.1 EJES PRINCIPALES
      // -------------------------
      svg
        .append("line")
        .attr("x1", center.x - radius)
        .attr("y1", center.y)
        .attr("x2", center.x + radius)
        .attr("y2", center.y)
        .attr("stroke", "#94A3B8")
        .attr("stroke-width", 1.5)
        .attr("opacity", 0.8);

      svg
        .append("line")
        .attr("x1", center.x)
        .attr("y1", center.y - radius)
        .attr("x2", center.x)
        .attr("y2", center.y + radius)
        .attr("stroke", "#94A3B8")
        .attr("stroke-width", 1.5)
        .attr("opacity", 0.8);
      // -------------------------
      // 2.2. ETIQUETAS DE EJES (usando style/attr individuales)
      // -------------------------

      const outerLabelOffsetY = 12; // distancia desde el borde del radar
      const outerLabelOffsetX = 30;
      const centerX = width / 2;
      const centerY = 15 + (1.02 * height) / 2;
      let outerLabels = [
        { text: "Flexibilidad", angle: -90 },
        { text: "Foco\nExterno", angle: 0 },
        { text: "Consistencia", angle: 90 },
        { text: "Foco\nInterno", angle: 180 },
      ];

      // grupo de labels externos
      svg
        .append("g")
        .attr("class", "outer-labels")
        .selectAll("text")
        .data(outerLabels)
        .enter()
        .append("text")
        .attr("text-anchor", "middle")
        .attr("font-size", fontSize)
        .attr("fill", "#333")
        .attr("x", (d) => {
          const rad = (d.angle * Math.PI) / 180;
          return centerX + (radius + outerLabelOffsetX) * Math.cos(rad);
        })
        .attr("y", (d) => {
          const rad = (d.angle * Math.PI) / 180;
          return centerY + (radius + outerLabelOffsetY) * Math.sin(rad);
        })
        .each(function(d) {
          const lines = d.text.split("\n");
          lines.forEach((line, i) => {
            d3.select(this)
              .append("tspan")
              .attr("x", d3.select(this).attr("x"))
              .attr("dy", i === 0 ? 0 : "1.2em")
              .text(line);
          });
        });

      // -------------------------
      // 3. GRID
      // -------------------------
      gridLevels.forEach((level) => {
        const polygon = angles.map((a) => {
          const r = scale(level);
          return [center.x + Math.cos(a) * r, center.y + Math.sin(a) * r];
        });

        svg
          .append("polygon")
          .attr("points", polygon.join(" "))
          .attr("fill", "none")
          .attr("stroke", "#CBD5E1")
          .attr("stroke-width", 1)
          .attr("opacity", 0.25);
      });

      // -------------------------
      // 4. EJES DIAGONALES
      // -------------------------
      angles.forEach((a) => {
        svg
          .append("line")
          .attr("x1", center.x)
          .attr("y1", center.y)
          .attr("x2", center.x + Math.cos(a) * radius)
          .attr("y2", center.y + Math.sin(a) * radius)
          .attr("stroke", "#CBD5E1")
          .attr("stroke-width", 1)
          .attr("opacity", 0.25);
      });

      // -------------------------
      // 5. NÚMEROS DE ESCALA
      // -------------------------
      gridLevels.forEach((level) => {
        angles.forEach((a) => {
          const r = scale(level);
          const x = center.x + Math.cos(a) * (r + 8);
          const y = center.y + Math.sin(a) * (r + 8);

          svg
            .append("text")
            .attr("x", x)
            .attr("y", y)
            .attr("text-anchor", "middle")
            .attr("dominant-baseline", "middle")
            .style("font-size", "9px")
            .style("fill", "#94A3B8")
            .text(level);
        });
      });

      // -------------------------
      // 6. ETIQUETAS CUADRANTES
      // -------------------------
      angles.forEach((a, i) => {
        svg
          .append("text")
          .attr("x", center.x + Math.cos(a) * (radius + 45))
          .attr("y", center.y + Math.sin(a) * (radius + 45))
          .attr("text-anchor", "middle")
          .attr("dominant-baseline", "middle")
          .style("font-size", fontSize)
          .style("font-weight", "600")
          .style("fill", labelColors[i])
          .text(categories[i]);
      });

      // -------------------------
      // 7. SERIES
      // -------------------------
      const drawPolygon = (values, stroke, fill, dashed = false) => {
        const points = angles.map((a, i) => {
          const r = scale(values[i]);
          return [center.x + Math.cos(a) * r, center.y + Math.sin(a) * r];
        });

        const pointsZero = angles.map(() => [center.x, center.y]);

        svg
          .append("polygon")
          .attr("points", pointsZero.join(" "))
          .attr("stroke", stroke)
          .attr("fill", fill)
          .attr("stroke-width", 2.3)
          .attr("stroke-dasharray", dashed ? "6 6" : "0")
          .attr("opacity", 0)
          .transition()
          .duration(600)
          .ease(d3.easeCubicOut)
          .attr("opacity", 0.55)
          .attrTween("points", () => (t) =>
            angles
              .map((a, i) => {
                const r = scale(values[i]) * t;
                return [center.x + Math.cos(a) * r, center.y + Math.sin(a) * r];
              })
              .join(" "),
          );

        return points;
      };

      const drawDots = (
        points,
        values,
        stroke,
        hollow = false,
        labelColor = stroke,
      ) => {
        points.forEach(([x, y], i) => {
          svg
            .append("circle")
            .attr("cx", x)
            .attr("cy", y)
            .attr("r", 4)
            .attr("fill", hollow ? "#fff" : stroke)
            .attr("stroke", hollow ? stroke : "none")
            .attr("stroke-width", hollow ? 2 : 0)
            .attr("opacity", 0)
            .style("cursor", "pointer")
            .on("mouseenter", (event) => {
              tooltip
                .style("opacity", 1)
                .style("left", event.clientX + 14 + "px")
                .style("top", event.clientY + 14 + "px")
                .html(
                  `<strong style="color:${labelColor}">${
                    hollow ? "Deseada" : "Actual"
                  }</strong><br>${categories[i]}: <strong>${
                    values[i]
                  }%</strong>`,
                );
            })
            .on("mousemove", (event) => {
              tooltip
                .style("left", event.clientX + 14 + "px")
                .style("top", event.clientY + 14 + "px");
            })
            .on("mouseleave", () => {
              tooltip.style("opacity", 0);
            })
            .transition()
            .duration(350)
            .delay(350)
            .ease(d3.easeLinear)
            .attr("opacity", 1);
        });
      };
      // === LEYENDA ===
      const legendData = [
        { name: "Actual", color: "#d04e8c" }, // color de puntos + relleno actual
        { name: "Deseada", color: "#4a90e2" }, // color de puntos + línea deseado
      ];

      const legendX = 10; // posición horizontal
      const legendY = 10; // posición vertical
      const legendSpacing = 80; // separación entre elementos
      if (this.size === "lg") {
        const legend = svg
          .append("g")
          .attr("class", "legend")
          .attr("transform", `translate(${legendX}, ${legendY})`);

        legend
          .selectAll("g")
          .data(legendData)
          .enter()
          .append("g")
          .attr("transform", (_, i) => `translate(${i * legendSpacing}, 0)`)
          .each(function(d) {
            const g = d3.select(this);

            // símbolo (círculo)
            g.append("circle")
              .attr("r", 7)
              .attr("cx", 0)
              .attr("cy", 0)
              .attr("fill", d.color)
              .attr("stroke", "#fff")
              .attr("stroke-width", 2);

            // texto
            g.append("text")
              .attr("x", 15)
              .attr("y", 5)
              .attr("font-size", fontSize)
              .attr("font-weight", 500)
              .attr("fill", "#333")
              .text(d.name);
          });
      }

      // Pasada 1: polígonos (debajo)
      const pointsActual = drawPolygon(
        this.actual,
        "#EC4899",
        "#EC489980",
        false,
      );
      const pointsDeseada = drawPolygon(
        this.deseada,
        "#3B82F6",
        "#3B82F620",
        true,
      );
      // Pasada 2: círculos (siempre encima de los polígonos)
      // #EC4899 (rosa) oscurecido → hsl(330°, 81%, 33%) ≈ #9d174d
      // #3B82F6 (azul) oscurecido → hsl(217°, 91%, 36%) ≈ #1d4ed8
      drawDots(pointsActual, this.actual, "#EC4899", false, "#9d174d");
      drawDots(pointsDeseada, this.deseada, "#3B82F6", true, "#1d4ed8");
    },
  },
};
</script>

<style scoped>
.cultura-chart {
  width: 100%;
  margin: 0 auto;
}
</style>



// WEBPACK FOOTER //
// src/components/cultura/sections/results/ResultsChart.vue